import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Calendar,
  Plus,
  Edit,
  Trash2,
  Archive,
  Check,
  X,
  Play,
  Star,
  AlertCircle,
  Clock,
  CheckCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import {
  getAcademicTerms,
  createAcademicTerm,
  updateAcademicTerm,
  deleteAcademicTerm,
  activateAcademicTerm,
  setCurrentAcademicTerm,
  archiveAcademicTerm,
} from "../../services/academicTerms";

interface AcademicTerm {
  _id: string;
  name: string;
  code: string;
  academicYear: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  isCurrent: boolean;
  status: "upcoming" | "active" | "completed" | "archived";
  description?: string;
  createdBy?: { firstName: string; lastName: string };
  createdAt: string;
}

const TermManagement: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedTerm, setSelectedTerm] = useState<AcademicTerm | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    academicYear: new Date().getFullYear().toString(),
    startDate: "",
    endDate: "",
    description: "",
  });

  const queryClient = useQueryClient();

  // Fetch academic terms
  const {
    data: termsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["academic-terms"],
    queryFn: getAcademicTerms,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

  // Debug: Log the data structure
  console.log("Terms Data:", termsData);
  console.log("Terms Array:", termsData?.data);
  console.log("Query Error:", error);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createAcademicTerm,
    onSuccess: () => {
      toast.success("Academic term created successfully");
      queryClient.invalidateQueries({ queryKey: ["academic-terms"] });
      handleCloseModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to create term");
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      updateAcademicTerm(id, data),
    onSuccess: () => {
      toast.success("Academic term updated successfully");
      queryClient.invalidateQueries({ queryKey: ["academic-terms"] });
      handleCloseModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to update term");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteAcademicTerm,
    onSuccess: () => {
      toast.success("Academic term deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["academic-terms"] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to delete term");
    },
  });

  // Activate mutation
  const activateMutation = useMutation({
    mutationFn: activateAcademicTerm,
    onSuccess: () => {
      toast.success("Academic term activated successfully");
      queryClient.invalidateQueries({ queryKey: ["academic-terms"] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to activate term");
    },
  });

  // Set current mutation
  const setCurrentMutation = useMutation({
    mutationFn: setCurrentAcademicTerm,
    onSuccess: () => {
      toast.success("Current term set successfully");
      queryClient.invalidateQueries({ queryKey: ["academic-terms"] });
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Failed to set current term"
      );
    },
  });

  // Archive mutation
  const archiveMutation = useMutation({
    mutationFn: archiveAcademicTerm,
    onSuccess: () => {
      toast.success("Academic term archived successfully");
      queryClient.invalidateQueries({ queryKey: ["academic-terms"] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to archive term");
    },
  });

  const handleCreateTerm = () => {
    setModalMode("create");
    setSelectedTerm(null);
    setFormData({
      name: "",
      code: "",
      academicYear: new Date().getFullYear().toString(),
      startDate: "",
      endDate: "",
      description: "",
    });
    setShowModal(true);
  };

  const handleEditTerm = (term: AcademicTerm) => {
    setModalMode("edit");
    setSelectedTerm(term);
    setFormData({
      name: term.name,
      code: term.code,
      academicYear: term.academicYear,
      startDate: term.startDate.split("T")[0],
      endDate: term.endDate.split("T")[0],
      description: term.description || "",
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTerm(null);
    setFormData({
      name: "",
      code: "",
      academicYear: new Date().getFullYear().toString(),
      startDate: "",
      endDate: "",
      description: "",
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (modalMode === "create") {
      createMutation.mutate(formData);
    } else if (selectedTerm) {
      updateMutation.mutate({ id: selectedTerm._id, data: formData });
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this term?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleActivate = (id: string) => {
    activateMutation.mutate(id);
  };

  const handleSetCurrent = (id: string) => {
    setCurrentMutation.mutate(id);
  };

  const handleArchive = (id: string) => {
    if (window.confirm("Are you sure you want to archive this term?")) {
      archiveMutation.mutate(id);
    }
  };

  const getStatusBadge = (term: AcademicTerm) => {
    const badges = [];

    if (term.isActive) {
      badges.push(
        <span
          key="active"
          className="px-3 py-1 bg-gradient-to-r from-green-400 to-green-500 text-white text-xs font-bold rounded-full flex items-center gap-1"
        >
          <Check className="w-3 h-3" /> Active
        </span>
      );
    }

    if (term.isCurrent) {
      badges.push(
        <span
          key="current"
          className="px-3 py-1 bg-gradient-to-r from-blue-400 to-blue-500 text-white text-xs font-bold rounded-full flex items-center gap-1"
        >
          <Star className="w-3 h-3" /> Current
        </span>
      );
    }

    const statusColors = {
      upcoming: "bg-gradient-to-r from-yellow-400 to-yellow-500",
      active: "bg-gradient-to-r from-green-400 to-green-500",
      completed: "bg-gradient-to-r from-gray-400 to-gray-500",
      archived: "bg-gradient-to-r from-slate-400 to-slate-500",
    };

    badges.push(
      <span
        key="status"
        className={`px-3 py-1 ${
          statusColors[term.status]
        } text-white text-xs font-bold rounded-full`}
      >
        {term.status.charAt(0).toUpperCase() + term.status.slice(1)}
      </span>
    );

    return <div className="flex flex-wrap gap-2">{badges}</div>;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const filteredTerms =
    termsData?.data?.filter((term: AcademicTerm) => {
      if (filterStatus === "all") return true;
      return term.status === filterStatus;
    }) || [];

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-purple-400 to-indigo-500 rounded-2xl p-4 shadow-lg">
                <Calendar className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-slate-900">
                  Academic Term Management
                </h1>
                <p className="text-slate-600 font-semibold mt-1">
                  Centralized management of academic terms across all branches
                </p>
              </div>
            </div>
            <button
              onClick={handleCreateTerm}
              className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white px-6 py-3 rounded-xl flex items-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 font-bold"
            >
              <Plus className="w-5 h-5" />
              Create Term
            </button>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 mb-6">
          <div className="flex items-center gap-4">
            <span className="text-slate-700 font-bold">Filter by Status:</span>
            <div className="flex gap-2">
              {["all", "upcoming", "active", "completed", "archived"].map(
                (status) => (
                  <button
                    key={status}
                    onClick={() => setFilterStatus(status)}
                    className={`px-4 py-2 rounded-xl font-semibold transition-all ${
                      filterStatus === status
                        ? "bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-lg"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                )
              )}
            </div>
          </div>
        </div>

        {/* Terms List */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredTerms.map((term: AcademicTerm) => (
            <div
              key={term._id}
              className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-6 py-4 border-b border-slate-200/60">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">
                      {term.name}
                    </h3>
                    <p className="text-sm text-slate-600 font-semibold mt-1">
                      {term.code} • {term.academicYear}
                    </p>
                  </div>
                  {getStatusBadge(term)}
                </div>
              </div>

              <div className="p-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-slate-700">
                    <Clock className="w-5 h-5 text-blue-500" />
                    <span className="font-semibold">
                      {formatDate(term.startDate)} - {formatDate(term.endDate)}
                    </span>
                  </div>

                  {term.description && (
                    <div className="flex items-start gap-3 text-slate-700">
                      <AlertCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm">{term.description}</p>
                    </div>
                  )}

                  {term.createdBy && (
                    <div className="text-sm text-slate-600 pt-2 border-t border-slate-200">
                      Created by {term.createdBy.firstName}{" "}
                      {term.createdBy.lastName}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 mt-6 pt-4 border-t border-slate-200">
                  {!term.isActive && term.status !== "archived" && (
                    <button
                      onClick={() => handleActivate(term._id)}
                      className="flex-1 bg-gradient-to-r from-green-400 to-green-500 hover:from-green-500 hover:to-green-600 text-white px-4 py-2 rounded-xl flex items-center justify-center gap-2 transition-all font-semibold text-sm shadow-md hover:shadow-lg"
                    >
                      <Play className="w-4 h-4" />
                      Activate
                    </button>
                  )}

                  {!term.isCurrent && term.status !== "archived" && (
                    <button
                      onClick={() => handleSetCurrent(term._id)}
                      className="flex-1 bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white px-4 py-2 rounded-xl flex items-center justify-center gap-2 transition-all font-semibold text-sm shadow-md hover:shadow-lg"
                    >
                      <Star className="w-4 h-4" />
                      Set Current
                    </button>
                  )}

                  {term.status !== "archived" && (
                    <button
                      onClick={() => handleEditTerm(term)}
                      className="bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white p-2 rounded-xl transition-all shadow-md hover:shadow-lg"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  )}

                  {term.status === "completed" &&
                    term.status !== "archived" && (
                      <button
                        onClick={() => handleArchive(term._id)}
                        className="bg-gradient-to-r from-slate-400 to-slate-500 hover:from-slate-500 hover:to-slate-600 text-white p-2 rounded-xl transition-all shadow-md hover:shadow-lg"
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                    )}

                  {!term.isActive && !term.isCurrent && (
                    <button
                      onClick={() => handleDelete(term._id)}
                      className="bg-gradient-to-r from-red-400 to-red-500 hover:from-red-500 hover:to-red-600 text-white p-2 rounded-xl transition-all shadow-md hover:shadow-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredTerms.length === 0 && (
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12 text-center">
            <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              No terms found
            </h3>
            <p className="text-slate-600">
              {filterStatus === "all"
                ? "Create your first academic term to get started"
                : `No ${filterStatus} terms available`}
            </p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">
                {modalMode === "create"
                  ? "Create Academic Term"
                  : "Edit Academic Term"}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-2">
                    Term Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                    required
                    placeholder="e.g., First Term 2024"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-2">
                    Term Code *
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        code: e.target.value.toUpperCase(),
                      })
                    }
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent uppercase"
                    required
                    placeholder="e.g., T1-2024"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Academic Year *
                </label>
                <input
                  type="text"
                  value={formData.academicYear}
                  onChange={(e) =>
                    setFormData({ ...formData, academicYear: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                  required
                  placeholder="e.g., 2024"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) =>
                      setFormData({ ...formData, startDate: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-2">
                    End Date *
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) =>
                      setFormData({ ...formData, endDate: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                  rows={3}
                  placeholder="Optional description..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 px-6 py-3 rounded-xl font-bold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                  className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "Saving..."
                    : modalMode === "create"
                    ? "Create Term"
                    : "Update Term"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TermManagement;
